import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { createNewsletterCampaign } from "@/lib/api/newsletter.functions";
import { AdminFormFooter } from "@/components/admin/AdminFormFooter";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { NewsletterAdminNav } from "@/components/admin/NewsletterAdminNav";
import { ContentAdminTabs } from "@/components/admin/ContentAdminTabs";
import { CraftEditor, type CraftEditorHandle } from "@/components/admin/CraftEditor";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { resolveCraftForSave } from "@/lib/craft/use-craft-save";

export const Route = createFileRoute("/admin/newsletter/newsletters/new")({
  component: NewNewsletterPage,
});

function NewNewsletterPage() {
  const navigate = useNavigate();
  const [subject, setSubject] = useState("");
  const [previewText, setPreviewText] = useState("");
  const [contentFormat, setContentFormat] = useState<"html" | "craft">("html");
  const [html, setHtml] = useState("<p>Hello,</p><p>Share your latest insight here.</p>");
  const [craftState, setCraftState] = useState("");
  const [craftCustomCss, setCraftCustomCss] = useState("");
  const [saving, setSaving] = useState(false);
  const craftEditorRef = useRef<CraftEditorHandle>(null);

  async function handleSave() {
    const craft = resolveCraftForSave(craftEditorRef, { craftState, craftCustomCss });
    setSaving(true);
    try {
      const row = await createNewsletterCampaign({
        data: {
          subject,
          previewText,
          contentFormat,
          contentHtml: html,
          craftState: craft.serialized,
          craftCustomCss: craft.customCss,
        },
      });
      toast.success("Newsletter draft created");
      navigate({ to: "/admin/newsletter/newsletters/$id", params: { id: row!.id } });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminPageShell>
      <AdminPageHeader title="New newsletter" />
      <NewsletterAdminNav />
      <ContentAdminTabs
        settingsLabel="Email settings"
        settingsForm={
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Newsletter subject line" />
            </div>
            <div className="space-y-2">
              <Label>Preview text (optional)</Label>
              <Input value={previewText} onChange={(e) => setPreviewText(e.target.value)} placeholder="Inbox preview snippet" />
            </div>
            <div className="space-y-2">
              <Label>Editor format</Label>
              <Select value={contentFormat} onValueChange={(v) => setContentFormat(v as "html" | "craft")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="html">Rich text (recommended for email)</SelectItem>
                  <SelectItem value="craft">Craft.js blocks (simple layouts)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Use rich text for best email client compatibility. Craft mode supports text, buttons, and images only.</p>
            </div>
          </div>
        }
        content={
          contentFormat === "html" ? (
            <RichTextEditor value={html} onChange={setHtml} />
          ) : (
            <CraftEditor
              ref={craftEditorRef}
              className="w-full"
              customCss={craftCustomCss}
              onChange={(serialized, css) => {
                setCraftState(serialized);
                setCraftCustomCss(css);
              }}
              onCustomCssChange={setCraftCustomCss}
            />
          )
        }
      />
      <AdminFormFooter cancelTo="/admin/newsletter/newsletters" onSave={handleSave} saving={saving} saveLabel="Create draft" savingLabel="Creating…" />
    </AdminPageShell>
  );
}
